import type { EncryptedBlob } from "../protocol/envelope.js";

export interface WalletSyncBlobResponse {
  version: number;
  ciphertext_base64?: string;
  content_nonce_base64?: string;
  key_derivation_hint?: string;
  device_id?: string;
  updated_at?: string;
  wallet_sync?: null;
}

export interface WalletSyncUploadResponse {
  version: number;
  updated_at: string;
}

export interface WalletSyncConflictResponse {
  error: string;
  current_version: number;
  current_blob: WalletSyncBlobResponse | null;
}

export interface RecoveryBackupResponse {
  ciphertext_base64?: string;
  content_nonce_base64?: string;
  key_derivation_hint?: string;
  updated_at?: string;
  backup?: null;
}

export interface TrustiiWalletClientConfig {
  baseUrl: string;
  getAccessToken: () => Promise<string>;
}

export class TrustiiApiClient {
  constructor(private readonly config: TrustiiWalletClientConfig) {}

  private async request<T>(
    path: string,
    init: RequestInit = {},
  ): Promise<T> {
    const token = await this.config.getAccessToken();
    const response = await fetch(`${this.config.baseUrl}${path}`, {
      ...init,
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });

    if (!response.ok) {
      const body = await response.text();
      throw new Error(`api_error_${response.status}:${body}`);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  async getWalletSync(): Promise<WalletSyncBlobResponse> {
    return this.request("/api/v1/wallet_sync");
  }

  async putWalletSync(
    body: EncryptedBlob,
    ifMatch?: number,
  ): Promise<WalletSyncUploadResponse | WalletSyncConflictResponse> {
    const token = await this.config.getAccessToken();
    const headers: Record<string, string> = {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
    if (ifMatch != null) {
      headers["If-Match"] = String(ifMatch);
    }

    const response = await fetch(`${this.config.baseUrl}/api/v1/wallet_sync`, {
      method: "PUT",
      headers,
      body: JSON.stringify(body),
    });

    const json = (await response.json()) as WalletSyncUploadResponse | WalletSyncConflictResponse;
    if (response.status === 409) {
      return json;
    }
    if (!response.ok) {
      throw new Error(`api_error_${response.status}:${JSON.stringify(json)}`);
    }
    return json;
  }

  async enrollSync(
    webauthnId: string,
    body: {
      sync_device_id: string;
      public_key_jwk: Record<string, string>;
      enrollment_proof: string;
      step_up_token: string;
    },
  ): Promise<{ enrolled: boolean; sync_device_id: string }> {
    return this.request(`/api/v1/devices/${encodeURIComponent(webauthnId)}/enroll_sync`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  async getMe(): Promise<Record<string, unknown>> {
    return this.request("/api/v1/me");
  }

  async getPersonas(): Promise<{ personas: Record<string, unknown>[] }> {
    return this.request("/api/v1/personas");
  }

  async createPersona(body: Record<string, unknown>): Promise<{ persona: Record<string, unknown> }> {
    return this.request("/api/v1/personas", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ persona: body }),
    });
  }

  async updatePersona(
    slug: string,
    body: Record<string, unknown>,
  ): Promise<{ persona: Record<string, unknown> }> {
    return this.request(`/api/v1/personas/${encodeURIComponent(slug)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ persona: body }),
    });
  }

  async setActivePersona(slug: string): Promise<{ success: boolean }> {
    return this.request("/api/v1/personas/active", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug }),
    });
  }

  async getDevices(): Promise<{ devices: Record<string, unknown>[] }> {
    return this.request("/api/v1/devices");
  }

  async deleteDevice(webauthnId: string): Promise<void> {
    await this.request(`/api/v1/devices/${encodeURIComponent(webauthnId)}`, {
      method: "DELETE",
    });
  }

  async uploadRecoveryBackup(body: {
    ciphertext_base64: string;
    content_nonce_base64: string;
    verification_tag: string;
    key_derivation_hint?: string;
  }): Promise<{ saved: boolean; updated_at: string }> {
    return this.request("/api/v1/encrypted_recovery_backup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  async getRecoveryBackup(): Promise<RecoveryBackupResponse> {
    return this.request("/api/v1/encrypted_recovery_backup");
  }
}
